
import { useEffect } from 'react';
import SEOHead from '../../components/seo/SEOHead';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: "Information We Collect",
      content: [
        "Personal Information: Name, address, phone number, email address, date of birth, and emergency contact information.",
        "Medical Information: Dental history, treatment records, X-rays, photographs, insurance information, and health conditions relevant to dental care.",
        "Payment Information: Billing address, payment method details, and insurance claim information.",
        "Website Usage: IP address, browser type, pages visited, and interaction data when using our website."
      ]
    },
    {
      title: "How We Use Your Information",
      content: [
        "Providing dental care services and treatment planning",
        "Scheduling appointments and sending appointment reminders",
        "Processing payments and insurance claims",
        "Communicating about your treatment and follow-up care",
        "Maintaining accurate medical records as required by law",
        "Improving our services and patient experience",
        "Complying with legal and regulatory requirements"
      ]
    },
    {
      title: "Information Sharing and Disclosure",
      content: [
        "Healthcare Providers: We may share your information with specialists, laboratories, or other healthcare providers involved in your care.",
        "Insurance Companies: We share necessary information for claim processing and pre-authorization.",
        "Legal Requirements: We may disclose information when required by law, court orders, or regulatory authorities.",
        "Emergency Situations: In medical emergencies, we may share relevant health information with emergency responders.",
        "Business Associates: We may share information with trusted third parties who assist in our operations (billing services, IT support) under strict confidentiality agreements."
      ]
    },
    {
      title: "Data Security",
      content: [
        "We implement appropriate technical and organizational security measures to protect your personal information.",
        "Our staff receives regular training on privacy and security protocols.",
        "We use secure, encrypted systems for storing and transmitting sensitive information.",
        "Access to patient information is restricted to authorized personnel only.",
        "We regularly review and update our security measures to ensure continued protection."
      ]
    },
    {
      title: "Your Rights",
      content: [
        "Access: You have the right to access your personal and medical information.",
        "Correction: You can request corrections to inaccurate or incomplete information.",
        "Restriction: You may request restrictions on how we use or disclose your information.",
        "Portability: You can request copies of your medical records in electronic format.",
        "Withdrawal: You may withdraw consent for certain uses of your information (where consent is the legal basis)."
      ]
    },
    {
      title: "Retention of Information",
      content: [
        "We retain patient records as required by Indian medical record retention laws and professional guidelines.",
        "Adult patient records are typically retained for a minimum of 10 years after the last treatment.",
        "Pediatric patient records are retained until the patient reaches age 25 or for 10 years after the last treatment, whichever is longer.",
        "Financial records are retained as required by tax and accounting regulations."
      ]
    },
    {
      title: "Website Privacy",
      content: [
        "Our website may use cookies to improve user experience and analyze website traffic.",
        "We do not sell or rent personal information collected through our website to third parties.",
        "Contact forms and appointment requests are transmitted securely and handled confidentially.",
        "We may use analytics tools to understand website usage patterns while protecting individual privacy."
      ]
    },
    {
      title: "Third-Party Services",
      content: [
        "We may use third-party services for appointment scheduling, payment processing, and communication.",
        "These services are selected based on their privacy and security standards.",
        "We ensure appropriate data protection agreements are in place with all third-party providers.",
        "Links to external websites are provided for convenience; we are not responsible for their privacy practices."
      ]
    },
    {
      title: "Children's Privacy",
      content: [
        "We provide dental care to patients of all ages, including children.",
        "For patients under 18, we obtain consent from parents or legal guardians for treatment and information sharing.",
        "We take special care to protect the privacy of pediatric patients and their families.",
        "Parents/guardians have the right to access and control their child's health information."
      ]
    },
    {
      title: "Changes to This Policy",
      content: [
        "We may update this privacy policy periodically to reflect changes in our practices or legal requirements.",
        "Significant changes will be communicated to patients through our website or direct notification.",
        "The effective date of the current policy is displayed at the top of this page.",
        "Continued use of our services after policy updates constitutes acceptance of the revised terms."
      ]
    }
  ];

  return (
    <>
      <SEOHead 
        title="Privacy Policy | The Tooth Doctor - Advanced Dental Care Centre"
        description="Read The Tooth Doctor's comprehensive privacy policy. Learn how we protect your personal and medical information, your rights, and our commitment to patient confidentiality in Chennai."
        keywords="privacy policy, patient confidentiality, dental records privacy, medical information protection, HIPAA compliance Chennai"
        canonical="https://toothdoctor.in/privacy-policy"
      />
      
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-[#012B53] to-[#014A7A] text-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 max-w-3xl mx-auto">
              Your privacy and the confidentiality of your medical information are of utmost importance to us
            </p>
            <div className="mt-6 text-sm text-blue-200">
              <p>Effective Date: January 1, 2024</p>
              <p>Last Updated: January 1, 2024</p>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-blue-50 border-l-4 border-[#14B8A6] p-6 rounded-r-lg mb-8">
              <h2 className="text-xl font-semibold text-[#012B53] mb-3">
                Our Commitment to Your Privacy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                At The Tooth Doctor - Advanced Dental Care Centre, we are committed to protecting the privacy and confidentiality of your personal and medical information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our clinic or use our services. We comply with all applicable Indian privacy laws and dental practice regulations.
              </p>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg mb-8">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                <i className="ri-information-line mr-2"></i>
                Important Notice
              </h3>
              <p className="text-yellow-700">
                By receiving treatment at our clinic or using our services, you consent to the collection and use of your information as described in this policy. If you have any questions or concerns, please contact us at the information provided below.
              </p>
            </div>
          </div>
        </section>

        {/* Privacy Policy Sections */}
        <section className="py-8 sm:py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              {sections.map((section, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <h3 className="text-xl font-semibold text-[#012B53] mb-4 flex items-center">
                    <span className="bg-[#14B8A6] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">
                      {index + 1}
                    </span>
                    {section.title}
                  </h3>
                  <ul className="space-y-3">
                    {section.content.map((item, itemIndex) => (
                      <li key={itemIndex} className="text-gray-700 leading-relaxed flex items-start">
                        <i className="ri-check-line text-[#14B8A6] mr-3 mt-1 flex-shrink-0"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-[#012B53] mb-6 text-center">
                Contact Us About Privacy Matters
              </h2>
              <p className="text-gray-700 text-center mb-8">
                If you have any questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="bg-[#14B8A6] text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <h3 className="font-semibold text-[#012B53] mb-2">Visit Us</h3>
                  <p className="text-gray-600 text-sm">
                    The Tooth Doctor - Advanced Dental Care Centre<br />
                    No.9/3, First street, Thambu Swamy Nagar<br />
                    Near Bus Stand, Thiruverkadu, Chennai
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="bg-[#14B8A6] text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <h3 className="font-semibold text-[#012B53] mb-2">Call Us</h3>
                  <p className="text-gray-600 text-sm">
                    <a href="tel:+916369090441" className="hover:text-[#14B8A6] transition-colors">
                      +91 6369090441
                    </a><br />
                    <a href="tel:+919884885014" className="hover:text-[#14B8A6] transition-colors">
                      +91 9884885014
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="text-center mt-6">
                <div className="bg-[#14B8A6] text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="font-semibold text-[#012B53] mb-2">Email Us</h3>
                <p className="text-gray-600">
                  <a href="mailto:toothdoctor.in@gmail.com" className="hover:text-[#14B8A6] transition-colors">
                    toothdoctor.in@gmail.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Legal Disclaimer */}
        <section className="py-8 bg-[#012B53] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-blue-200 text-sm leading-relaxed mb-4">
              This Privacy Policy is governed by Indian law and regulations. Any disputes arising from this policy will be subject to the jurisdiction of Chennai courts. We reserve the right to update this policy as needed to comply with changing legal requirements or business practices.
            </p>
            <div className="border-t border-blue-700 pt-4">
              <p className="text-blue-300 text-xs">
                Website designed and developed by{' '}
                <a 
                  href="https://techmejora.com/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-[#14B8A6] hover:text-white transition-colors font-medium"
                >
                  Tech Mejora
                </a>
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
